import React, { useEffect, useState } from 'react';
import { string, number, array, object, func, bool } from 'prop-types';
import classNames from 'classnames';
import { l_chevron_left, l_chevron_right } from '@fiverr-private/icons';
import { env } from '@fiverr-private/futile';
import { createCarouselBreakpoints } from './utils';
import { DEFAULT_SLIDES_TO_SHOW, BREAKPOINTS, SCROLL_TYPE, DIRECTIONS, SWIPE_MODE_INDICATOR } from './constants';
import Arrow from './arrow';
import { SlidesIndicator } from './SlidesIndicator';
import { useScroll } from './hooks/useScroll';
import useDidMount from './hooks/useDidMount';

import './index.scss';

const getDisplayedContentSizes = () => {
    let width = 0;
    let height = 0;
    if (env.browser) {
        width = document.documentElement?.clientWidth || 0;
        height = document.documentElement?.clientHeight || 0;
    }
    return { width, height };
};

/**
 * @deprecated Please use Carousel from @fiverr-private/carousel instead.
 */
const Carousel = ({
    slidesToShow,
    slides,
    breakpoints,
    scrollType,
    slidePadding,
    swipeMode,
    isInfinite,
    carouselClassName,
    trackClassName,
    slideClassName,
    arrowsClassName,
    slidesIndicatorClassName,
    withSlidesIndicator,
    arrowSize,
    navigationRef,
    onScroll,
    onArrowClick,
    autoSlidesWidth,
    hideDefaultArrows,
    Component,
    componentProps,
    swipeModeIndicator
}) => {

    const {
        scrollRight,
        scrollLeft,
        scrollToPosition,
        resetCarousel,
        onTransitionEnd,
        checkTrackPosition,
        updateCounterPositionOnScrollBySwipe,
        currentPosition,
        supportSlidesIndicator,
        carouselTransformValue,
        slidesTrackRef,
        canScrollLeft,
        slidesListRef,
        canScrollRight } = useScroll({ scrollType, slides, slidesToShow, onArrowClick, isInfinite, withSlidesIndicator, swipeMode, showSlidesIndicatorInSwipeMode: swipeModeIndicator === SWIPE_MODE_INDICATOR.DOTS });

    const didMount = useDidMount();
    const canScrollOnInfinite = !!(isInfinite && (canScrollLeft || canScrollRight));

    const trackStyle = breakpoints && createCarouselBreakpoints(breakpoints, slidesToShow);

    const carouselStyle = {
        '--slide-padding': `${slidePadding}px`,
        '--slides-to-show': slidesToShow
    };

    const trackClassNames = classNames(
        'slides-track',
        { 'auto-slides-width': autoSlidesWidth },
        { 'responsive': breakpoints },
        { 'swipe-mode': swipeMode },
        { 'dot-indicator': swipeModeIndicator === SWIPE_MODE_INDICATOR.DOTS },
        trackClassName
    );

    const carouselClassNames = classNames(
        'carousel-wrapper',
        { 'responsive': breakpoints },
        { 'swipe-mode': swipeMode },
        carouselClassName
    );

    const [displayedContentSizes, setDisplayedContentDimension] = useState(getDisplayedContentSizes());

    useEffect(() => {
        const handleResize = () => {

            // On mobile devices, every time that the user scroll the screen up or down then the search bar appear/disappear,
            // and it changes the window.innerHeight and this trigger the resize event, even though that the screen doesn't resize
            // the solution is to check if the content-size really changed
            const currentDisplayedContentSizes = getDisplayedContentSizes();
            const dimensionsHasBeenChanged = (currentDisplayedContentSizes.height !== displayedContentSizes.height) ||
                (currentDisplayedContentSizes.width !== displayedContentSizes.width);

            if (dimensionsHasBeenChanged) {
                setDisplayedContentDimension(currentDisplayedContentSizes);
                resetCarousel();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (didMount) {
            onScroll({ canScrollLeft, canScrollRight, canScrollOnInfinite, currentPosition });
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carouselTransformValue.current, canScrollLeft, canScrollRight, currentPosition]);

    useEffect(() => {
        checkTrackPosition();
    }, [slides, checkTrackPosition]);

    useEffect(() => {
        if (navigationRef) {
            navigationRef.current = { scrollRight, scrollLeft, scrollToPosition };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollLeft, scrollRight, scrollToPosition]);

    const onSwipe = (event) => {
        if (swipeModeIndicator === SWIPE_MODE_INDICATOR.DOTS) {
            updateCounterPositionOnScrollBySwipe(event);
        }
    };

    return (
        <div style={carouselStyle} className={carouselClassNames}>
            <div className="slides-list" ref={slidesListRef}>
                { !hideDefaultArrows && (
                    <Arrow className={classNames(arrowsClassName, { 'clickable': canScrollLeft || canScrollOnInfinite })}
                        icon={l_chevron_left}
                        iconSize={arrowSize}
                        direction={DIRECTIONS.LEFT}
                        onScroll={scrollLeft}/>
                )}
                { !hideDefaultArrows && (
                    <Arrow className={classNames(arrowsClassName, { 'clickable': canScrollRight || canScrollOnInfinite })}
                        icon={l_chevron_right}
                        iconSize={arrowSize}
                        direction={DIRECTIONS.RIGHT}
                        onScroll={scrollRight}/>
                )}
                {supportSlidesIndicator && (
                    <SlidesIndicator className={slidesIndicatorClassName}
                        slides={slides}
                        slidesToShow={slidesToShow}
                        currentPosition={currentPosition}/>
                )}
                <div className={trackClassNames} onTransitionEnd={onTransitionEnd} style={trackStyle} ref={slidesTrackRef} onScroll={onSwipe}>
                    {slides.map((item, index) => (
                        <div key={index} className={classNames('slide', slideClassName)}>
                            <Component index={index} currentPosition={currentPosition} {...item} {...componentProps}/>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

Carousel.displayName = 'Carousel';

Carousel.propTypes = {

    /**
     * Array of slides which will be presented inside the carousel
     */
    slides: array,

    /**
     * If exists it determines how many slides will be visible on every media query
     */
    breakpoints: object,

    /**
     * If true the carousel will work in infinite mode
     */
    isInfinite: bool,

    /**
     * If true slides navigation will be on swipe and not by arrow click.
     */
    swipeMode: bool,

    /**
     * Props that will be passed to each slide component.
     */
    componentProps: object,

    /**
     * Determines how many slides will be scrolled on each click.
     */
    scrollType: string,

    /**
     * The number of visible slides on default.
     */
    slidesToShow: number,

    /**
     * If true slides min-width will be set to auto.
     */
    autoSlidesWidth: bool,

    /**
     * If true default carousel arrows won't be rendered.
     */
    hideDefaultArrows: bool,

    /**
     * The padding between each slide.
     */
    slidePadding: number,

    /**
      * Class that will be applied to the carousel element.
      */
    carouselClassName: string,

    /**
      * Class that will be applied to the track element.
      */
    trackClassName: string,

    /**
     * Class that will be applied to each slide element.
     */
    slideClassName: string,

    /**
     * Add a custom class prop for the dots
     */
    slidesIndicatorClassName: string,
    /**
     * Show current slide position indicator
     */
    withSlidesIndicator: bool,
    /**
      * Class that will be applied to the arrow elements.
      */
    arrowsClassName: string,

    /**
     * The arrow icon's size, in pixels.
     */
    arrowSize: number,
    /**
     * A callback function that will be called after every change.
     */
    onScroll: func,

    /**
     * A callback function that will be called on every arrow click.
     */
    onArrowClick: func,

    /**
     * A ref than when passed can be used to call scrollLeft & scrollRight from parent.
     */
    navigationRef: object,

    /**
     * Component will render for every slide.
     */
    Component: func.isRequired,

    /**
     * Set page position of the slides
     */
    position: number,

    /**
     * Determine which kind of indicator will appear in swipe mode.
     */
    swipeModeIndicator: SWIPE_MODE_INDICATOR
};

Carousel.defaultProps = {
    slidesToShow: DEFAULT_SLIDES_TO_SHOW,
    slides: [],
    autoSlidesWidth: false,
    withSlidesIndicator: false,
    swipeMode: false,
    slidePadding: 16,
    scrollType: SCROLL_TYPE.SINGLE,
    componentProps: {},
    slidesIndicatorClassName: '',
    carouselClassName: '',
    arrowsClassName: '',
    slideClassName: '',
    onScroll: Function.prototype,
    onArrowClick: Function.prototype,
    swipeModeIndicator: SWIPE_MODE_INDICATOR.HALF_CARD
};

Object.assign(Carousel, {
    SCROLL_TYPE,
    BREAKPOINTS,
    SWIPE_MODE_INDICATOR
});

export default Carousel;
